import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";

export const MAX_IMAGE_SIZE_MB = 20;

export function selectImageFile(
  onValidFile: (dataUrl: string, file: File) => void,
) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/jpeg,image/png,image/jpg";

  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, JPG, and PNG are allowed.");
      return;
    }

    const maxBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(
        `File size too large. Maximum allowed size is ${MAX_IMAGE_SIZE_MB} MB.`,
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = () => onValidFile(reader.result as string, file);
    reader.readAsDataURL(file);
  };

  input.click();
}

export async function compressImage(file: File): Promise<File> {
  if (file.type === "image/svg+xml") {
    return file;
  }

  const targetSizeBytes = 0.5 * 1024 * 1024;
  if (file.size <= targetSizeBytes) {
    return file;
  }

  const normalizedType = file.type.toLowerCase();
  const isPng = normalizedType === "image/png";
  const isJpeg =
    normalizedType === "image/jpeg" || normalizedType === "image/jpg";
  if (!isPng && !isJpeg) {
    return file;
  }

  const outputType = isPng ? "image/png" : "image/jpeg";

  try {
    const compressed = await imageCompression(file, {
      maxSizeMB: 0.5,
      useWebWorker: true,
      fileType: outputType,
      initialQuality: 0.9,
    });

    const baseName = file.name.replace(/\.[^/.]+$/, "");
    const nextName = baseName
      ? `${baseName}${isPng ? ".png" : ".jpg"}`
      : `image${isPng ? ".png" : ".jpg"}`;

    return new File([compressed], nextName, { type: outputType });
  } catch {
    return file;
  }
}

export function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [header, base64] = dataUrl.split(",");

  if (!header || !base64) {
    throw new Error("Invalid data URL");
  }

  const mimeMatch = header.match(/:(.*?);/);
  const mimeType = mimeMatch?.[1] ?? "image/png";

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], fileName, {
    type: mimeType,
    lastModified: Date.now(),
  });
}
